import { getCustomerBagByCountry, Bag, getBagById } from "@repo/apis/bag";
import { ChameleonRequest, is404Error } from "@repo/chameleon-request";
import { ExpandOptions } from "../../apis/src/commerce/bag/utils/expand";
import {
  addProductInitialiser,
  getItemCountInitialiser,
  getTotalPriceInitialiser,
  getProductsInitialiser,
  removeProductInitialiser,
  updateProductInitialiser,
  initialiseCheckoutInitialiser,
  getSubtotalPriceInitialiser,
  getNextDayDeliveryInitialiser,
  getFreeDeliveryQualificationInitialiser,
  updateBagWithExpansionsInitialiser,
} from "./public-methods";
import {
  deleteAllBagCookies,
  getBagCookieValues,
  setBagCookies,
} from "./utils/bag-cookies";
import { initialiseMethod } from "./utils/method-initialiser";
import type { BagState } from "./utils/state.types";

export type BagSdkConfig = {
  customerId: string;
  chameleonRequestInstance: ChameleonRequest;
  getAuthHeaderPredicate: () => Promise<string>;
  lang: string;
  keyStoreDataversion: string;
  store: string;
  country: string;
  originCountry: string;
  currency: string;
  sizeSchema: string;
  initialBagExpansions: ExpandOptions;
  shop: string;
};

export type BagSdkInstance = {
  initialBag: BagState;
  addProduct: ReturnType<typeof addProductInitialiser>;
  removeProduct: ReturnType<typeof removeProductInitialiser>;
  getTotalPrice: ReturnType<typeof getTotalPriceInitialiser>;
  getItemCount: ReturnType<typeof getItemCountInitialiser>;
  getProducts: ReturnType<typeof getProductsInitialiser>;
  updateProduct: ReturnType<typeof updateProductInitialiser>;
  initialiseCheckout: ReturnType<typeof initialiseCheckoutInitialiser>;
  getSubtotalPrice: ReturnType<typeof getSubtotalPriceInitialiser>;
  getNextDayDelivery: ReturnType<typeof getNextDayDeliveryInitialiser>;
  getFreeDeliveryQualification: ReturnType<
    typeof getFreeDeliveryQualificationInitialiser
  >;
  updateBagWithExpansions: ReturnType<
    typeof updateBagWithExpansionsInitialiser
  >;
};

export const initialiseBagSdk = async ({
  customerId,
  chameleonRequestInstance,
  lang,
  keyStoreDataversion,
  store,
  country,
  originCountry,
  sizeSchema,
  getAuthHeaderPredicate,
  currency,
  initialBagExpansions,
  shop,
}: BagSdkConfig): Promise<BagSdkInstance> => {
  const authHeader = await getAuthHeaderPredicate();
  const isSignedOut =
    window.sessionStorage.getItem("AsosAuth.SignedOut") === "true" || false;

  if (isSignedOut) {
    deleteAllBagCookies({ store, country });
    window.sessionStorage.removeItem("AsosAuth.SignedOut");
  }

  let bag: BagState = null;
  let bagSessionToken: string | null = null;
  let currentBagExpansions = initialBagExpansions;
  const { bagId: bagIdCookie } = getBagCookieValues({
    store,
    country,
  });

  try {
    const response = bagIdCookie
      ? await getBagById({
          chameleonRequestInstance,
          store,
          lang,
          bagId: bagIdCookie,
          expand: currentBagExpansions,
          authHeader,
        })
      : await getCustomerBagByCountry({
          chameleonRequestInstance,
          store,
          lang,
          customerId,
          country,
          keyStoreDataversion,
          authHeader,
          expand: currentBagExpansions,
        });

    bagSessionToken = response.bagSessionToken;
    bag = response.data.bag;

    setBagCookies(
      { store, country },
      {
        bagId: bag.id,
        bagOrigin: bag.origin,
        sessionToken: bagSessionToken,
      },
    );
  } catch (error) {
    if (!is404Error(error)) {
      throw error;
    }
  }

  const getBagSessionToken = () => bagSessionToken;
  const setBagSessionToken = (token: string) => {
    bagSessionToken = token;
  };

  const getBag = () => bag;
  const setBag = (newBag: Bag | null) => {
    bag = newBag;
  };

  const getBagExpansions = () => currentBagExpansions;
  const setBagExpansions = (newExpansions: ExpandOptions) => {
    currentBagExpansions = newExpansions;
  };

  const context = {
    customerId,
    chameleonRequestInstance,
    getAuthHeaderPredicate,
    lang,
    keyStoreDataversion,
    store,
    country,
    originCountry,
    currency,
    sizeSchema,
    shop,
    initialBagExpansions,
    state: {
      getBagSessionToken,
      setBagSessionToken,
      getBag,
      setBag,
      getBagExpansions,
      setBagExpansions,
    },
  };

  return {
    initialBag: bag,
    addProduct: initialiseMethod(context, addProductInitialiser),
    updateBagWithExpansions: initialiseMethod(
      context,
      updateBagWithExpansionsInitialiser,
    ),
    removeProduct: initialiseMethod(context, removeProductInitialiser),
    getTotalPrice: initialiseMethod(context, getTotalPriceInitialiser),
    getItemCount: initialiseMethod(context, getItemCountInitialiser),
    getProducts: initialiseMethod(context, getProductsInitialiser),
    updateProduct: initialiseMethod(context, updateProductInitialiser),
    initialiseCheckout: initialiseMethod(
      context,
      initialiseCheckoutInitialiser,
    ),
    getSubtotalPrice: initialiseMethod(context, getSubtotalPriceInitialiser),
    getNextDayDelivery: initialiseMethod(
      context,
      getNextDayDeliveryInitialiser,
    ),
    getFreeDeliveryQualification: initialiseMethod(
      context,
      getFreeDeliveryQualificationInitialiser,
    ),
  };
};
